// client/src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  signIn,
  confirmSignIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useToast } from '@chakra-ui/react';
import {
  messageForWrongAuthMethod,
  messageForOAuthTokenFailure,
  parseWrongAuthMethodResponse,
} from '../utils/authErrors';

const API_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
const AuthContext = createContext(null);

let _cachedTokenGetter = null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authConflict, setAuthConflict] = useState(null);
  const [googleSigningIn, setGoogleSigningIn] = useState(false);

  const toast = useToast();
  const shownConflictRef = useRef(null);
  const googleLoginInFlightRef = useRef(false);

  const getAccessToken = useCallback(async (forceRefresh = false) => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    _cachedTokenGetter = getAccessToken;
    return () => {
      _cachedTokenGetter = null;
    };
  }, [getAccessToken]);

  const buildConflict = useCallback((existingProvider, overrideMessage = null) => {
    const provider = existingProvider === 'google' ? 'google' : 'local';
    return {
      existingProvider: provider,
      message: overrideMessage || messageForWrongAuthMethod(provider),
    };
  }, []);

  const showAuthConflictToast = useCallback(
    (conflict) => {
      if (!conflict) return;

      const key = `${conflict.existingProvider}:${conflict.message}`;
      if (shownConflictRef.current === key) return;

      shownConflictRef.current = key;

      toast({
        title: 'Sign-in method mismatch',
        description:
          conflict.message || messageForWrongAuthMethod(conflict.existingProvider),
        status: 'warning',
        duration: 8000,
        isClosable: true,
        position: 'top',
      });

      setAuthConflict(null);
    },
    [toast]
  );

  useEffect(() => {
    if (authConflict) showAuthConflictToast(authConflict);
  }, [authConflict, showAuthConflictToast]);

  const raiseAuthConflict = useCallback(
    async (conflict, options = {}) => {
      if (!conflict) return null;

      const { signOutFirst = false, throwError = false } = options;

      if (signOutFirst) {
        try {
          await signOut();
        } catch {
          /* ignore */
        }
      }

      setUser(null);
      setAuthConflict(conflict);

      if (throwError) {
        const err = new Error(conflict.message);
        err.code = 'WRONG_AUTH_METHOD';
        err.existingProvider = conflict.existingProvider;
        throw err;
      }

      return {
        conflict: true,
        existingProvider: conflict.existingProvider,
        message: conflict.message,
      };
    },
    []
  );

  const getConflictFromMessage = useCallback(
    (rawMessage) => {
      const normalizedRaw =
        typeof rawMessage === 'string'
          ? rawMessage
          : rawMessage?.message || rawMessage?.error?.message || '';

      const msg = messageForOAuthTokenFailure({ message: normalizedRaw });

      if (/sign in with google|registered with google/i.test(msg)) {
        return {
          conflict: buildConflict('google'),
          msg,
        };
      }

      if (/sign in with email|email sign-in|email instead|email[_ -]?otp/i.test(msg)) {
        return {
          conflict: buildConflict('local'),
          msg,
        };
      }

      return { conflict: null, msg };
    },
    [buildConflict]
  );

  const handleOAuthFailure = useCallback(
    async (rawMessage, options = {}) => {
      const { conflict, msg } = getConflictFromMessage(rawMessage);

      if (conflict) {
        return await raiseAuthConflict(conflict, {
          signOutFirst: false,
          throwError: false,
        });
      }

      if (!/^canceled$/i.test(String(msg || '').trim())) {
        toast({
          title: 'Google sign-in failed',
          description: msg || 'Authentication failed.',
          status: 'error',
          duration: 6000,
          isClosable: true,
          position: 'top',
        });
      }

      return null;
    },
    [getConflictFromMessage, raiseAuthConflict, toast]
  );

  const handleWrongAuthMethod = useCallback(
    async (body) => {
      const parsed = parseWrongAuthMethodResponse(body);
      if (!parsed) return null;

      const conflict = buildConflict(parsed.existingProvider, parsed.message);

      return await raiseAuthConflict(conflict, {
        signOutFirst: true,
        throwError: true,
      });
    },
    [buildConflict, raiseAuthConflict]
  );

  const checkAuth = useCallback(async () => {
    try {
      await getCurrentUser();

      const token = await getAccessToken();
      if (!token) throw new Error('No token');

      const res = await fetch(`${API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        await handleWrongAuthMethod(body);
      }

      if (res.status === 400 || res.status === 401) {
        try {
          await signOut();
        } catch {
          /* ignore */
        }
      }

      if (!res.ok) throw new Error('Not authenticated');

      const data = await res.json();
      setUser(data);
      setAuthConflict(null);
      shownConflictRef.current = null;
      return data;
    } catch (err) {
      if (err.code === 'WRONG_AUTH_METHOD') {
        return {
          conflict: true,
          existingProvider: err.existingProvider,
          message: err.message,
        };
      }

      setUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, [getAccessToken, handleWrongAuthMethod]);

  const requestOTP = useCallback(async (email) => {
    try {
      await signOut();
    } catch {
      /* ignore */
    }

    shownConflictRef.current = null;

    const normalizedEmail = email.trim().toLowerCase();

    const res = await fetch(`${API_URL}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail }),
    });

    if (res.status === 409) {
      const body = await res.json().catch(() => ({}));
      const parsed = parseWrongAuthMethodResponse(body);
      const conflict = parsed
        ? buildConflict(parsed.existingProvider, parsed.message)
        : buildConflict(body?.existingProvider);

      await raiseAuthConflict(conflict, {
        signOutFirst: false,
        throwError: true,
      });
    }

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to send OTP');
    }

    let result = await signIn({
      username: normalizedEmail,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
    });

    if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
      result = await confirmSignIn({ challengeResponse: 'EMAIL_OTP' });
    }

    if (result.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      throw new Error(`Unexpected auth step: ${result.nextStep?.signInStep}`);
    }

    return { success: true };
  }, [buildConflict, raiseAuthConflict]);

  const verifyOTP = useCallback(async (otp) => {
    const result = await confirmSignIn({ challengeResponse: otp });
    if (!result.isSignedIn) throw new Error('Sign in incomplete');
    return await checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await signOut();
      fetch(`${API_URL}/auth/logout`, { method: 'POST' }).catch(() => {});
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setAuthConflict(null);
      shownConflictRef.current = null;
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    if (googleLoginInFlightRef.current) return null;

    googleLoginInFlightRef.current = true;
    shownConflictRef.current = null;
    setAuthConflict(null);
    setGoogleSigningIn(true);

    try {
      await signInWithRedirect({
        provider: 'Google',
        options: {
          prompt: 'SELECT_ACCOUNT',
        },
      });

      return null;
    } catch (err) {
      if (err.name === 'OAuthSignInException' && err.message === 'canceled') {
        return null;
      }

      return await handleOAuthFailure(err);
    } finally {
      googleLoginInFlightRef.current = false;
      setGoogleSigningIn(false);
    }
  }, [handleOAuthFailure]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (!error && !errorDescription) return;

    const raw = decodeURIComponent(errorDescription || error || '')
      .replace(/\+/g, ' ')
      .trim();

    if (!raw) return;

    handleOAuthFailure(raw);

    const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [handleOAuthFailure]);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect_failure') {
        const raw = payload?.data?.message || payload?.data?.error?.message;
        if (!raw || /^canceled$/i.test(String(raw).trim())) return;
        handleOAuthFailure(raw);
        return;
      }

      if (payload.event === 'signInWithRedirect' || payload.event === 'signedIn') {
        checkAuth();
      }
    });

    return unsubscribe;
  }, [checkAuth, handleOAuthFailure]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        authLoading,
        authConflict,
        googleSigningIn,
        requestOTP,
        verifyOTP,
        logout,
        loginWithGoogle,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export const fetchWithAuth = async (url, options = {}) => {
  const getToken = _cachedTokenGetter;

  const buildHeaders = (token) => ({
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  const token = getToken ? await getToken() : null;
  let res = await fetch(url, { ...options, headers: buildHeaders(token) });

  if (res.status === 401) {
    const freshToken = getToken ? await getToken(true) : null;
    if (freshToken) {
      res = await fetch(url, { ...options, headers: buildHeaders(freshToken) });
    }
    if (res.status === 401) window.location.href = '/signin';
  }

  return res;
};